/**
 * 追従カメラ（docs/05_rendering-and-audio.md 10 章）。中継のカメラマンのように、選手を斜め後ろ上方から追う。
 *
 * - 距離は「近・中・遠」の 3 段階で固定（歩き・飛行で値を変える）。
 * - 角度は、候補をいくつも試して点数の良いものを選ぶ（0.25 秒ごと）：
 *   選手が地形に隠れない・選手の手前に山肌が映り込まない・斜め後ろ上方の自然な構図に近い・今の角度から大きく変えない。
 * - 選手は時間の圧縮で速く動くので、カメラは選手に固定した位置関係で動き（位置の遅れはつけない）、角度だけをなめらかに変える。
 * - 進む向きは、選手が進んだ「距離」でならした方位（ジグザグの山道や、サーマルでの旋回で振られない）。
 */
import * as THREE from 'three';

export type CameraPreset = 'near' | 'mid' | 'far';
export const CAMERA_PRESETS: readonly CameraPreset[] = ['near', 'mid', 'far'];
export const CAMERA_PRESET_LABEL: Record<CameraPreset, string> = { near: '近', mid: '中', far: '遠' };

export interface FollowTarget {
  /** 選手の位置（歩き：足元、飛行：パイロット） */
  pos: THREE.Vector3;
  flying: boolean;
  /** 注視点の高さ（pos からの m。選手の見た目の大きさに合わせる） */
  lookUp: number;
  /** 向きの手がかり（離陸準備中は離陸の向き）。null なら進んだ向きから決める */
  headingHint: number | null;
}

export interface CameraPose {
  position: THREE.Vector3;
  look: THREE.Vector3;
}

interface Shot {
  /** 距離（m） */
  dist: number;
  /** 好ましい見下ろしの角度（度） */
  el: number;
}

/** 距離と角度（歩き・飛行） */
const SHOTS: Record<CameraPreset, { walk: Shot; fly: Shot }> = {
  near: { walk: { dist: 12, el: 10 }, fly: { dist: 32, el: 6 } },
  // 中：少し引いて、やや俯瞰
  mid: { walk: { dist: 140, el: 26 }, fly: { dist: 300, el: 20 } },
  // 遠：上空からの広い俯瞰（ヘリの空撮のように、谷や山並みごと見せる）
  far: { walk: { dist: 1500, el: 38 }, fly: { dist: 2500, el: 32 } },
};
/** 真後ろから横へずらす角度（斜め後ろの構図） */
const SIDE_DEG = 25;
/** 角度の候補（進む向きに対する横のずれ・見下ろしの足し分、度） */
const OFF_CANDIDATES = [0, 15, -15, 30, -30, 45, -45, 65, -65, 90, -90, 120, -120, 150, -150, 180];
const EL_CANDIDATES = [-4, 0, 6, 12, 20, 30, 42, 58];
/** これより大きく角度を変えるときは、振らずにカット（瞬時に切り替え）する（度） */
const CUT_DEG = 70;
/** 追従カメラの縦の視野角（度）。地図（45°）より広くして、景色を広く見せる */
export const FOLLOW_FOV_DEG = 60;
const HALF_FOV = THREE.MathUtils.degToRad(FOLLOW_FOV_DEG / 2);
/** 選手を画面の下寄りに置くため、選手への向きより上へ傾ける最小の角度 */
const FRAME_UP = THREE.MathUtils.degToRad(11);
/** 空を入れるため、画面の中心をこの角度（水平より下）まで上げる。選手が画面の下の縁から MIN_BOTTOM 以上離れる範囲で */
const SKY_PITCH = THREE.MathUtils.degToRad(-12);
const MIN_BOTTOM = THREE.MathUtils.degToRad(8);
/** カットのあと、この時間（現実の秒）は大きく角度を変えない（ひとつのショットを保つ）。隠れたときは例外 */
const MIN_SHOT_S = 4;
/** 角度を選ぶとき、この時間（現実の秒）先の選手の位置からも見通しを確かめる */
const PREDICT_S = 0.5;
/** 候補を選び直す間隔（現実の秒） */
const EVAL_S = 0.25;
/** 進む向きをならす距離（m） */
const HEADING_DIST = { walk: 250, fly: 700 };
/** カメラの向きが変わる速さの上限（度/秒）と、なめらかさ（秒） */
const PAN_MAX_DEG_S = 45;
const PAN_TAU_S = 0.5;
/** 画面の縦の視野角の半分の tan と横長の比 */
const TAN_V = Math.tan(THREE.MathUtils.degToRad(FOLLOW_FOV_DEG / 2));
const ASPECT = 1.75;

/** 奥行きを測る距離（選手までの距離の何倍か）。これより遠くまで抜ければ十分に開けている */
const OPEN_STEPS = [1.2, 1.6, 2.2, 3, 4, 5.5, 7.5, 10];
const OPEN_FAR = 10;

const DEG = Math.PI / 180;
const wrapDeg = (d: number): number => ((((d + 180) % 360) + 360) % 360) - 180;

/**
 * 視線の良し悪し：
 * - blocked：カメラから選手への線が地形に遮られるか
 * - foreground：選手より手前に山肌がどれだけ映るか（0〜1）
 * - openness：画面の上半分の奥行き（遠くの景色や空が見えるほど 1。すぐ後ろに斜面の壁が迫ると 0）
 */
export function viewQuality(
  groundAt: (x: number, z: number) => number,
  cam: THREE.Vector3,
  anchor: THREE.Vector3,
  look: THREE.Vector3,
): { blocked: boolean; blockedCount: number; foreground: number; slack: number; openness: number } {
  const dist = cam.distanceTo(anchor);
  // 遮り：選手のすぐそば（最後の 1 割）は地面に近くて当然なので見ない
  const margin = 0.3 + dist * 0.01;
  let blockedCount = 0;
  // 視線と地形のすき間の最小値（距離に対する比）。小さいと、選手が少し動いただけで隠れる
  let slack = Infinity;
  for (let k = 1; k <= 12; k++) {
    const t = (k / 12) * 0.9;
    const x = cam.x + (anchor.x - cam.x) * t;
    const y = cam.y + (anchor.y - cam.y) * t;
    const z = cam.z + (anchor.z - cam.z) * t;
    const gap = y - groundAt(x, z) - margin;
    if (gap < 0) blockedCount++;
    slack = Math.min(slack, gap / dist);
  }
  const blocked = blockedCount > 0;
  // 映り込み：選手の高さから上の 15 本の視線（選手への向きを基準にする）が、選手より手前で地形に当たる割合
  const fa = new THREE.Vector3().subVectors(anchor, cam).normalize();
  const ra = new THREE.Vector3().crossVectors(fa, THREE.Object3D.DEFAULT_UP).normalize();
  const ua = new THREE.Vector3().crossVectors(ra, fa);
  const d = new THREE.Vector3();
  let hits = 0;
  let total = 0;
  for (const su of [-0.8, -0.4, 0, 0.4, 0.8]) {
    for (const sv of [0, 0.3, 0.6]) {
      total++;
      d.copy(fa)
        .addScaledVector(ra, su * TAN_V * ASPECT)
        .addScaledVector(ua, sv * TAN_V)
        .normalize();
      for (let k = 1; k <= 8; k++) {
        const s = dist * (0.12 + (0.83 * k) / 8);
        const x = cam.x + d.x * s;
        const z = cam.z + d.z * s;
        if (cam.y + d.y * s < groundAt(x, z)) {
          hits++;
          break;
        }
      }
    }
  }
  // 奥行き：画面（注視点の向き）の上半分の 9 本の視線が、どこまで遠くへ抜けるか（選手までの距離の何倍か）
  const f = new THREE.Vector3().subVectors(look, cam).normalize();
  const r = new THREE.Vector3().crossVectors(f, THREE.Object3D.DEFAULT_UP).normalize();
  const u = new THREE.Vector3().crossVectors(r, f);
  let open = 0;
  let openN = 0;
  for (const su of [-0.6, 0, 0.6]) {
    for (const sv of [0.4, 0.7, 1.0]) {
      openN++;
      d.copy(f)
        .addScaledVector(r, su * TAN_V * ASPECT)
        .addScaledVector(u, sv * TAN_V)
        .normalize();
      let reach = OPEN_STEPS[OPEN_STEPS.length - 1] as number;
      for (const m of OPEN_STEPS) {
        const s = dist * m;
        if (cam.y + d.y * s < groundAt(cam.x + d.x * s, cam.z + d.z * s)) {
          reach = m;
          break;
        }
      }
      open += THREE.MathUtils.clamp((reach - 1.2) / (OPEN_FAR - 1.2), 0, 1);
    }
  }
  return { blocked, blockedCount, foreground: hits / total, slack, openness: open / openN };
}

export class FollowCamera {
  /** 距離の段階（近・中・遠） */
  preset: CameraPreset = 'mid';
  /** ホイールでの微調整（距離に掛ける） */
  zoom = 1;
  /** ドラッグでの回り込み（ラジアン）と見下ろしの足し分（ラジアン）。好みの角度として扱う */
  yaw = 0;
  pitch = 0;

  private readonly pose: CameraPose = { position: new THREE.Vector3(), look: new THREE.Vector3() };
  private readonly sample = new THREE.Vector3();
  private heading = 0;
  /** カメラが実際に向いている方位（heading を、速さの上限つきでなめらかに追う） */
  private view = 0;
  private dist = 40;
  /** 今の角度と目標の角度（進む向きに対する横のずれ・見下ろし、度） */
  private off = SIDE_DEG;
  private el = 16;
  private goalOff = SIDE_DEG;
  private goalEl = 16;
  private evalTimer = 0;
  /** 今のショット（最後のカットから）の長さ（現実の秒） */
  private shotAge = 0;
  private init = false;
  /** 直前の更新でカットしたか・その理由（検証ツール用） */
  lastCut = false;
  lastCutReason: 'blocked' | 'turn' | 'scene' | null = null;
  /** 直前の更新で向きの手がかり（離陸の向き）があったか */
  private hadHint = false;
  private readonly anchor = new THREE.Vector3();
  /** 選手の速度（現実の時間で。少し先の位置の予測に使う）と、直前の位置・注視点の高さ・飛行中か */
  private readonly vel = new THREE.Vector3();
  private readonly lastPos = new THREE.Vector3();
  private lookUp = 1;
  private flying = false;
  private readonly tmpCam = new THREE.Vector3();
  private readonly tmpLook = new THREE.Vector3();

  constructor(private readonly groundAt: (x: number, z: number) => number) {}

  /** 次に呼ばれたとき、補間せずにすぐその位置へ置く（別の選手に切り替えたときなど） */
  reset(): void {
    this.init = false;
    this.resetCut = true;
  }
  private resetCut = false;

  /** 今の注視点とカメラの位置（検証ツール用） */
  get current(): { off: number; el: number; dist: number; heading: number } {
    return { off: this.off, el: this.el, dist: this.dist, heading: this.view };
  }

  update(target: FollowTarget, realDt: number): CameraPose {
    const dt = Math.max(realDt, 1e-3);
    const shot = SHOTS[this.preset][target.flying ? 'fly' : 'walk'];
    const wantDist = shot.dist * this.zoom;
    const elPref = THREE.MathUtils.clamp(shot.el + this.pitch / DEG, 2, 70);
    const clearance = Math.max(2, wantDist * 0.08);

    // 進む向き：進んだ距離でならす（手がかりがあればそちらへ）
    const moved = Math.hypot(target.pos.x - this.sample.x, target.pos.z - this.sample.z);
    if (!this.init || moved > 3000) {
      this.sample.copy(target.pos);
      this.heading = target.headingHint ?? this.heading;
      this.view = this.heading;
      this.dist = wantDist;
    } else if (target.headingHint !== null) {
      this.heading += wrapRad(target.headingHint - this.heading) * (1 - Math.exp(-dt / 0.6));
      this.sample.copy(target.pos);
    } else if (moved > 5) {
      const dir = Math.atan2(target.pos.x - this.sample.x, -(target.pos.z - this.sample.z));
      const span = HEADING_DIST[target.flying ? 'fly' : 'walk'];
      this.heading += wrapRad(dir - this.heading) * (1 - Math.exp(-moved / span));
      this.sample.copy(target.pos);
    }
    // 離陸準備に入った瞬間は、パイロットの後ろのショットへ 1 回だけカット（振り向く途中で山に遮られないように）
    let sceneCut = false;
    if (this.init && target.headingHint !== null && !this.hadHint) {
      this.heading = target.headingHint;
      this.view = this.heading;
      sceneCut = true;
    }
    this.hadHint = target.headingHint !== null;
    this.dist += (wantDist - this.dist) * (1 - Math.exp(-dt / 0.35));
    // パン：なめらかに、速すぎないように
    const dv = wrapRad(this.heading - this.view) * (1 - Math.exp(-dt / PAN_TAU_S));
    const maxStep = PAN_MAX_DEG_S * DEG * dt;
    this.view += THREE.MathUtils.clamp(dv, -maxStep, maxStep);

    this.anchor.set(target.pos.x, target.pos.y + target.lookUp, target.pos.z);
    this.lookUp = target.lookUp;
    this.flying = target.flying;
    if (this.init && moved <= 3000) {
      const k = 1 - Math.exp(-dt / 0.3);
      this.vel.x += ((target.pos.x - this.lastPos.x) / dt - this.vel.x) * k;
      this.vel.y += ((target.pos.y - this.lastPos.y) / dt - this.vel.y) * k;
      this.vel.z += ((target.pos.z - this.lastPos.z) / dt - this.vel.z) * k;
    } else this.vel.set(0, 0, 0);
    this.lastPos.copy(target.pos);

    // 角度の選び直し（一定間隔、または今の角度で選手が隠れたとき）
    this.evalTimer -= dt;
    const now = this.place(this.off, this.el, this.tmpCam, this.tmpLook);
    const nowQ = viewQuality(this.groundAt, now, this.anchor, this.tmpLook);
    const tooLow = now.y < this.groundAt(now.x, now.z) + clearance;
    this.lastCut = false;
    this.lastCutReason = null;
    this.shotAge += dt;
    if (!this.init || sceneCut || this.evalTimer <= 0 || nowQ.blocked || tooLow) {
      this.evalTimer = EVAL_S;
      // ショットを始めたばかりなら、大きな角度の変更（カットになるもの）は選ばない
      const free = !this.init || sceneCut || nowQ.blocked || this.shotAge >= MIN_SHOT_S;
      this.choose(elPref, clearance, free ? Infinity : CUT_DEG);
      // 選手が隠れた・場面が変わった・大きく角度を変える、ときは振らずにカット（中継のカメラの切り替えと同じ）。
      // 地面に近すぎるだけなら、カットせずにカメラを持ち上げる（下で）
      const diff = Math.hypot(wrapDeg(this.goalOff - this.off), this.goalEl - this.el);
      if (!this.init || sceneCut || nowQ.blocked || diff > CUT_DEG) {
        this.lastCut = this.init || this.resetCut;
        this.resetCut = false;
        this.shotAge = 0;
        this.lastCutReason = sceneCut ? 'scene' : nowQ.blocked ? 'blocked' : 'turn';
        this.off = this.goalOff;
        this.el = this.goalEl;
      }
    }

    // 角度をなめらかに目標へ
    const k = 1 - Math.exp(-dt / 0.6);
    this.off += wrapDeg(this.goalOff - this.off) * k;
    this.el += (this.goalEl - this.el) * k;
    // 地面に近すぎるなら、必要な分だけ見下ろしを上げる（クレーンで持ち上げるように）
    const need = this.minElevation(this.off, this.el, clearance);
    if (this.el < need) {
      this.el = need;
      if (this.goalEl < need) this.goalEl = need;
    }

    this.place(this.off, this.el, this.pose.position, this.pose.look);
    // 最後の安全策：地面より下には置かない
    const floor = this.groundAt(this.pose.position.x, this.pose.position.z) + clearance * 0.5;
    if (this.pose.position.y < floor) this.pose.position.y = floor;
    this.init = true;
    return this.pose;
  }

  /** 横のずれ off で、カメラが地面から clearance 以上になる見下ろしの角度（度） */
  private minElevation(off: number, el: number, clearance: number): number {
    let e = el;
    for (let i = 0; i < 3; i++) {
      const cam = this.place(off, e, this.tmpCam, this.tmpLook);
      const need = this.groundAt(cam.x, cam.z) + clearance - this.anchor.y;
      const sin = THREE.MathUtils.clamp(need / this.dist, -1, 0.99);
      const req = Math.asin(sin) / DEG;
      if (req <= e + 0.05) break;
      e = Math.min(80, req);
    }
    return e;
  }

  /** 横のずれ off・見下ろし el（度）のカメラ位置と注視点 */
  private place(off: number, el: number, cam: THREE.Vector3, look: THREE.Vector3): THREE.Vector3 {
    const view = this.view + this.yaw + off * DEG;
    const e = el * DEG;
    const horiz = this.dist * Math.cos(e);
    cam.set(
      this.anchor.x - Math.sin(view) * horiz,
      this.anchor.y + this.dist * Math.sin(e),
      this.anchor.z + Math.cos(view) * horiz,
    );
    // 構図：選手を画面の下寄り（下から約 3 分の 1）に置き、前方（画面の上）を広く見せる。
    // 選手への向きを FRAME_UP だけ上へ傾け、進む向きへも少しずらす（進む側に余白を取る）
    const lead = this.dist * 0.08;
    const tx = this.anchor.x + Math.sin(this.view) * lead - cam.x;
    const tz = this.anchor.z - Math.cos(this.view) * lead - cam.z;
    const ty = this.anchor.y - cam.y;
    const h = Math.hypot(tx, tz);
    // 選手への向き（見下ろし）から FRAME_UP 以上は上へ傾け、空が入るまで（SKY_PITCH まで）さらに上げる。
    // ただし選手が画面の下の縁から MIN_BOTTOM より近づくほどは上げない
    const toAthlete = Math.atan2(ty, h);
    const pitch = Math.max(toAthlete + FRAME_UP, Math.min(SKY_PITCH, toAthlete + HALF_FOV - MIN_BOTTOM));
    const len = Math.hypot(h, ty);
    look.set(
      cam.x + (tx / Math.max(h, 1e-6)) * Math.cos(pitch) * len,
      cam.y + Math.sin(pitch) * len,
      cam.z + (tz / Math.max(h, 1e-6)) * Math.cos(pitch) * len,
    );
    return cam;
  }

  /** 候補から角度を選ぶ。limit：今の角度からこれ以上離れた候補は選ばない（度） */
  private choose(elPref: number, clearance: number, limit: number): void {
    const side = this.goalOff >= 0 ? 1 : -1;
    const prefOff = side * SIDE_DEG;
    let best = Infinity;
    let bestOff = this.goalOff;
    let bestEl = this.goalEl;
    // どの角度でも隠れるときは、隠れの一番少ない角度にする
    let leastBlocked = Infinity;
    let fallbackOff = this.goalOff;
    let fallbackEl = Math.max(this.goalEl, 45);
    const cam = new THREE.Vector3();
    const look = new THREE.Vector3();
    // 少し先の選手の位置（歩きは地面の上、飛行は今の上昇・下降のまま）
    const now = this.anchor.clone();
    const future = now.clone().addScaledVector(this.vel, PREDICT_S);
    if (!this.flying) future.y = this.groundAt(future.x, future.z) + this.lookUp;
    const futureCam = new THREE.Vector3();
    const futureLook = new THREE.Vector3();
    for (const off of OFF_CANDIDATES) {
      for (const dEl of EL_CANDIDATES) {
        const el = THREE.MathUtils.clamp(elPref + dEl, 2, 75);
        if (Math.hypot(wrapDeg(off - this.off), el - this.el) > limit) continue;
        this.place(off, el, cam, look);
        if (cam.y < this.groundAt(cam.x, cam.z) + clearance) continue;
        const q = viewQuality(this.groundAt, cam, this.anchor, look);
        if (q.blocked) {
          const c = q.blockedCount + 0.01 * Math.abs(wrapDeg(off - this.goalOff));
          if (c < leastBlocked) {
            leastBlocked = c;
            fallbackOff = off;
            fallbackEl = el;
          }
          continue;
        }
        // 少し先でも選手が隠れない角度にする（隠れるたびにカットが入らないように）
        this.anchor.copy(future);
        this.place(off, el, futureCam, futureLook);
        const q2 = viewQuality(this.groundAt, futureCam, future, futureLook);
        this.anchor.copy(now);
        let score = 80 * q.foreground;
        if (q2.blocked) score += 35;
        // 選手の向こうに斜面の壁が迫る（画面が山肌だけになる）角度は避け、遠くの景色が見える角度にする
        score += 60 * (1 - q.openness);
        // 視線のすき間が狭い角度は、少し動いただけで隠れてカットが増えるので避ける
        if (q.slack < 0.08) score += 150 * (0.08 - q.slack);
        // 斜め後ろを好むが、登り斜面などでは前から（選手の上側から谷を背景に）撮ってもよい
        score += 0.15 * Math.abs(wrapDeg(off - prefOff));
        score += el > elPref ? 0.6 * (el - elPref) : 1.0 * (elPref - el);
        score += 0.2 * Math.abs(wrapDeg(off - this.goalOff)) + 0.2 * Math.abs(el - this.goalEl);
        if (score < best) {
          best = score;
          bestOff = off;
          bestEl = el;
        }
      }
    }
    if (best === Infinity) {
      bestOff = fallbackOff;
      bestEl = fallbackEl;
    }
    this.goalOff = bestOff;
    this.goalEl = bestEl;
  }
}

function wrapRad(a: number): number {
  return Math.atan2(Math.sin(a), Math.cos(a));
}
